/**
 * Format dimensions to display with one decimal place
 * @param value - The dimension value in mm
 * @param useInches - Whether to display in inches (true) or mm (false)
 * @returns Formatted dimension string
 */
export const formatDimensionWithUnits = (value: number, useInches: boolean): string => {
  if (useInches) {
    // Convert mm to inches (1 inch = 25.4 mm)
    return (value / 25.4).toFixed(1);
  }
  return value.toFixed(1);
};

/**
 * Parse dimension input considering current unit
 * @param value - The input string value
 * @param useInches - Whether the input is in inches (true) or mm (false)
 * @returns Parsed dimension value in mm
 */
export const parseDimensionWithUnits = (value: string, useInches: boolean): number => {
  const parsed = parseFloat(value);
  if (useInches) {
    // Convert inches to mm
    return parsed * 25.4;
  }
  return parsed;
};

/**
 * Get the display value for a dimension input
 * @param axis - The dimension axis ('x', 'y', or 'z')
 * @param editingDimensions - Current editing state for dimensions
 * @param dimensions - Current model dimensions
 * @param useInches - Whether to display in inches
 * @returns The display value string
 */
export const getDimensionDisplayValue = (
  axis: 'x' | 'y' | 'z',
  editingDimensions: Record<'x' | 'y' | 'z', string | null>,
  dimensions: { x: number; y: number; z: number } | null,
  useInches: boolean
): string => {
  // If currently editing this dimension, show the editing value
  if (editingDimensions[axis] !== null) {
    return editingDimensions[axis]!;
  }

  // Otherwise show the formatted dimension
  if (!dimensions) return '';
  return formatDimensionWithUnits(dimensions[axis], useInches);
};

/**
 * Check if a dimension value has changed from its original value
 * @param axis - The dimension axis ('x', 'y', or 'z')
 * @param currentDimensions - Current model dimensions
 * @param originalDimensions - Original model dimensions
 * @param modelId - Model ID for storage lookup
 * @returns Whether the value has changed
 */
export const isValueChanged = (
  axis: 'x' | 'y' | 'z',
  currentDimensions: { x: number; y: number; z: number } | null,
  originalDimensions: { x: number; y: number; z: number } | undefined,
  modelId: string
): boolean => {
  if (!currentDimensions || !originalDimensions) {
    return false;
  }

  // Get the true original dimensions from storage if available
  let trueOriginalDimensions = { ...originalDimensions };

  try {
    const storageKey = `model_dimensions_${modelId}`;
    const savedDataString = localStorage.getItem(storageKey);

    if (savedDataString) {
      const savedData = JSON.parse(savedDataString);
      if (savedData && savedData.trueOriginalDimensions) {
        trueOriginalDimensions = savedData.trueOriginalDimensions;
      }
    }
  } catch (e) {
    // Ignore storage errors, use what we have
  }

  // Round to 1 decimal place to avoid minor floating point differences
  const originalValue = Math.round(trueOriginalDimensions[axis] * 10) / 10;
  const currentValue = Math.round(currentDimensions[axis] * 10) / 10;

  return originalValue !== currentValue;
}; 